import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { format, parseISO } from "date-fns";
import { TriangleIcon } from "@icons";
import { AuditLogDiff } from "@common/types/auditLog";
import { fullDateLongFormat } from "@common/helpers/date";
import { getAccountingBasisLabel } from "@common/helpers/accountingBasis";
import { formatRecurringRuleLabel } from "@common/helpers/recurringJournalEntries";
import { addTimeZoneOffset } from "@helpers/date";
import { useLedgers } from "@api/ledger.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { DocumentName } from "@components/documents/DocumentName";
import { getDiffEntry } from "../../utils/getDiffEntry";
import styles from "./HistoryJournalEntrySidebar.module.scss";

interface DocInfo {
    id: number;
    name: string;
}

interface Props {
    reference?: string;
    diffs: AuditLogDiff[];
    resource?: Record<string, unknown>;
    entityId?: number;
}

function buildFallbackDiff(
    field: string,
    resource?: Record<string, unknown>,
): AuditLogDiff | undefined {
    const value = resource?.[field];
    if (value == null) {
        return undefined;
    }
    return { field, before: { value: null }, after: { value } };
}

function formatDiffDisplay(
    val: unknown,
    formatter?: (v: unknown) => string,
): string {
    if (val == null) {
        return "";
    }
    if (formatter) {
        return formatter(val);
    }
    return String(val);
}

function formatDateValue(val: unknown): string {
    if (val == null) {
        return "";
    }
    try {
        const date =
            typeof val === "string" ? parseISO(val) : new Date(val as number);
        return format(addTimeZoneOffset(date), fullDateLongFormat);
    } catch {
        return String(val);
    }
}

interface DiffFieldRowProps {
    label: string;
    diff: AuditLogDiff | undefined;
    emptyLabel?: string;
    formatValue?: (v: unknown) => string;
}

const DiffFieldRow: React.FC<DiffFieldRowProps> = ({
    label,
    diff,
    emptyLabel,
    formatValue,
}) => {
    if (!diff) {
        return null;
    }

    const beforeStr = formatDiffDisplay(
        diff.before?.valueLabel ?? diff.before?.value,
        formatValue,
    );
    const afterStr = formatDiffDisplay(
        diff.after?.valueLabel ?? diff.after?.value,
        formatValue,
    );
    const hasChange = beforeStr !== afterStr;

    return (
        <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>{label}</span>
            <div className={styles.diffContainer}>
                {hasChange && beforeStr && (
                    <span className={styles.diffOld}>{beforeStr}</span>
                )}
                <span
                    className={
                        afterStr ? styles.fieldValue : styles.fieldValueMuted
                    }
                >
                    {afterStr ?? emptyLabel ?? ""}
                </span>
            </div>
        </div>
    );
};

export const HistoryJournalEntrySidebar: React.FC<Props> = ({
    reference,
    diffs,
    resource,
    entityId,
}) => {
    const [detailsOpen, setDetailsOpen] = useState(true);
    const [attachmentsOpen, setAttachmentsOpen] = useState(true);
    const { data: ledgers } = useLedgers(entityId);
    const { data: members } = useCurrentWorkspaceMembers();

    const ledgersById = useMemo(() => {
        if (!ledgers) {
            return {};
        }
        return Object.fromEntries(
            ledgers.map((l) => [l.id, getAccountingBasisLabel(l.basis)]),
        );
    }, [ledgers]);

    const membersById = useMemo(() => {
        if (!members) {
            return {};
        }
        return Object.fromEntries(members.map((m) => [m.user.id, m.user]));
    }, [members]);

    const dateDiff = useMemo(
        () =>
            getDiffEntry(diffs, "date") ?? buildFallbackDiff("date", resource),
        [diffs, resource],
    );
    const memoDiff = useMemo(
        () =>
            getDiffEntry(diffs, "memo") ?? buildFallbackDiff("memo", resource),
        [diffs, resource],
    );
    const reversedAtDiff = useMemo(
        () =>
            getDiffEntry(diffs, "reversedAt") ??
            buildFallbackDiff("reversedAt", resource),
        [diffs, resource],
    );
    const recurringRuleDiff = useMemo(() => {
        const labelDiff =
            getDiffEntry(diffs, "recurringRuleLabel") ??
            buildFallbackDiff("recurringRuleLabel", resource);

        if (labelDiff) {
            return labelDiff;
        }

        const recurringRule = resource?.recurringRule as
            | Record<string, unknown>
            | null
            | undefined;
        const idDiff =
            getDiffEntry(diffs, "recurringRuleId") ??
            buildFallbackDiff("recurringRuleId", resource);

        if (!idDiff && !recurringRule) {
            return undefined;
        }

        const label =
            recurringRule &&
            typeof recurringRule === "object" &&
            "period" in recurringRule
                ? formatRecurringRuleLabel(
                      recurringRule as Parameters<
                          typeof formatRecurringRuleLabel
                      >[0],
                  )
                : "";

        if (idDiff) {
            return {
                ...idDiff,
                after: {
                    ...idDiff.after,
                    value: idDiff.after?.value ? label || "Enabled" : null,
                },
                before: {
                    ...idDiff.before,
                    value: idDiff.before?.value
                        ? (idDiff.before.valueLabel ?? label ?? "Enabled")
                        : null,
                },
            };
        }

        if (label) {
            return {
                field: "recurringRuleId",
                before: { value: null },
                after: { value: label },
            };
        }

        return undefined;
    }, [diffs, resource]);
    const ledgerDiff = useMemo(() => {
        const raw = getDiffEntry(diffs, "accountingLedgerId");
        if (raw) {
            return {
                ...raw,
                before: {
                    ...raw.before,
                    value:
                        ledgersById[raw.before?.value as string] ??
                        raw.before?.value,
                },
                after: {
                    ...raw.after,
                    value:
                        ledgersById[raw.after?.value as string] ??
                        raw.after?.value,
                },
            };
        }
        const resourceLedgerId = resource?.accountingLedgerId as
            | string
            | undefined;
        if (resourceLedgerId) {
            return {
                field: "accountingLedgerId",
                before: { value: null },
                after: {
                    value: ledgersById[resourceLedgerId] ?? resourceLedgerId,
                },
            };
        }
        return undefined;
    }, [diffs, ledgersById, resource]);
    const creatorUser = useMemo(() => {
        const diff = getDiffEntry(diffs, "creatorUserId");
        const userId =
            diff?.after?.value ??
            diff?.before?.value ??
            resource?.creatorUserId;
        if (!userId) {
            return undefined;
        }
        return membersById[Number(userId)];
    }, [diffs, membersById, resource]);

    const attachments = useMemo(() => {
        const docDiff = getDiffEntry(diffs, "financialDocument");
        const hasDiff = docDiff != null;

        const afterDocs =
            (docDiff?.after?.value as DocInfo[] | undefined) ?? [];
        const beforeDocs =
            (docDiff?.before?.value as DocInfo[] | undefined) ?? [];

        let currentDocs: DocInfo[];
        if (hasDiff) {
            currentDocs = afterDocs;
        } else {
            const links = resource?.financialDocumentLinks as
                | Array<{
                      financialDocument?: { id: number; fileName: string };
                      financialDocumentId: number;
                  }>
                | undefined;
            currentDocs = (links ?? []).map((link) => ({
                id: link.financialDocumentId,
                name:
                    link.financialDocument?.fileName ??
                    `Document ${link.financialDocumentId}`,
            }));
        }

        if (!hasDiff) {
            return { removed: [], added: [], unchanged: currentDocs };
        }

        const beforeIds = new Set(beforeDocs.map((d) => d.id));
        const afterIds = new Set(currentDocs.map((d) => d.id));

        const removed = beforeDocs.filter((d) => !afterIds.has(d.id));
        const added = currentDocs.filter((d) => !beforeIds.has(d.id));
        const unchanged = currentDocs.filter((d) => beforeIds.has(d.id));

        return { removed, added, unchanged };
    }, [resource, diffs]);

    const hasFieldChanges = useMemo(
        () =>
            diffs.some((d) => d.field !== "lines") ||
            !!dateDiff ||
            !!memoDiff ||
            !!reversedAtDiff ||
            !!recurringRuleDiff ||
            !!ledgerDiff ||
            !!creatorUser,
        [
            diffs,
            dateDiff,
            memoDiff,
            reversedAtDiff,
            recurringRuleDiff,
            ledgerDiff,
            creatorUser,
        ],
    );

    return (
        <div className={styles.sidebar}>
            <div className={styles.sidebarSection}>
                <div
                    className={styles.sectionHeader}
                    onClick={() => setDetailsOpen(!detailsOpen)}
                >
                    <span>Details</span>
                    <TriangleIcon
                        className={classNames(styles.sectionToggle, {
                            [styles.collapsed]: !detailsOpen,
                        })}
                    />
                </div>

                {detailsOpen && (
                    <>
                        {reference && (
                            <div className={styles.fieldRow}>
                                <span className={styles.fieldLabel}>
                                    Reference
                                </span>
                                <span className={styles.fieldValue}>
                                    {reference}
                                </span>
                            </div>
                        )}

                        <DiffFieldRow
                            label="Date"
                            diff={dateDiff}
                            formatValue={formatDateValue}
                        />
                        <DiffFieldRow
                            label="Reverse date"
                            diff={reversedAtDiff}
                            formatValue={formatDateValue}
                        />
                        <DiffFieldRow
                            label="Recurrence"
                            diff={recurringRuleDiff}
                            emptyLabel="None"
                        />
                        <DiffFieldRow
                            label="Accounting ledger"
                            diff={ledgerDiff}
                        />
                        {creatorUser && (
                            <div className={styles.fieldRow}>
                                <span className={styles.fieldLabel}>
                                    Creator
                                </span>
                                <div className={styles.creatorRow}>
                                    <UserAvatar
                                        user={{
                                            name: creatorUser.name,
                                            avatar: creatorUser.avatar,
                                            iconColor: creatorUser.iconColor,
                                            email: creatorUser.email,
                                        }}
                                        size="xs"
                                        aria-hidden
                                    />
                                    <span className={styles.fieldValue}>
                                        {creatorUser.preferredName ??
                                            creatorUser.name}
                                    </span>
                                </div>
                            </div>
                        )}

                        {!hasFieldChanges && !reference && (
                            <span className={styles.fieldValueMuted}>
                                No field changes
                            </span>
                        )}
                    </>
                )}
            </div>

            <div className={styles.sidebarSection}>
                <DiffFieldRow label="Memo" diff={memoDiff} />
                {!memoDiff && (
                    <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Memo</span>
                        <span className={styles.fieldValueMuted}>-</span>
                    </div>
                )}
            </div>

            <div className={styles.sidebarSection}>
                <div
                    className={styles.sectionHeader}
                    onClick={() => setAttachmentsOpen(!attachmentsOpen)}
                >
                    <span>Attachments</span>
                    <TriangleIcon
                        className={classNames(styles.sectionToggle, {
                            [styles.collapsed]: !attachmentsOpen,
                        })}
                    />
                </div>

                {attachmentsOpen && (
                    <div className={styles.attachmentsList}>
                        {attachments.removed.map((file) => (
                            <span
                                key={`removed-${file.id}`}
                                className={styles.attachmentOld}
                            >
                                <DocumentName
                                    fileName={file.name}
                                    icon
                                    documentId={file.id}
                                />
                            </span>
                        ))}
                        {attachments.added.map((file) => (
                            <span
                                key={`added-${file.id}`}
                                className={styles.attachmentNew}
                            >
                                <DocumentName
                                    fileName={file.name}
                                    icon
                                    documentId={file.id}
                                />
                            </span>
                        ))}
                        {attachments.unchanged.map((file) => (
                            <span
                                key={file.id}
                                className={styles.attachmentLink}
                            >
                                <DocumentName
                                    fileName={file.name}
                                    icon
                                    documentId={file.id}
                                />
                            </span>
                        ))}
                        {attachments.added.length === 0 &&
                            attachments.removed.length === 0 &&
                            attachments.unchanged.length === 0 && (
                                <span className={styles.fieldValueMuted}>
                                    No attachments
                                </span>
                            )}
                    </div>
                )}
            </div>
        </div>
    );
};
